/**
 * Gift With Purchase (threshold-based)
 * ------------------------------------
 * Auto-adds a $0 gift variant once the cart's qualifying subtotal reaches the
 * market's threshold, renders the line as "FREE" with locked quantity/remove
 * controls, and removes it again when the cart drops back below the threshold.
 *
 * Pairs with:
 *   - snippets/gwp.liquid      (renders #gwp-config from the Market metafield)
 *   - snippets/cart-item.liquid (server-renders FREE on the native surfaces)
 *
 * Config comes from two metafields on the **Market** resource —
 * `custom.gwp_threshold` and `custom.gwp_product` — so the threshold and the
 * gift are both per-market. The gift's variant id is derived from the product
 * reference. See the comment block in `snippets/gwp.liquid`.
 *
 * The start/end window this file honours is not wired to a metafield today;
 * `gwp.liquid` emits zeroes for it. Scheduling is manual — see
 * `docs/gwp-setup.md`.
 *
 * Behavior contract:
 *   - Qualifying subtotal = `cart.total_price` minus the gift line minus the
 *     shipping-protection SKU. It is therefore *after* automatic discounts.
 *     Discount **codes** are applied at checkout, after the cart, so they are
 *     invisible here — a code that drops the order under the threshold will
 *     not remove the gift. Enforcing that requires a Shopify Function.
 *   - We never add more than 1 unit of the gift.
 *   - An empty cart (no items other than the gift and protection) always
 *     removes the gift, even if a stale threshold would still pass.
 *   - Outside the campaign window we only ever remove, never add — so a
 *     full-page-cached response can't resurrect an expired promo.
 *   - The gift line carries `_kp_auto_gwp` (hidden, engine ownership marker)
 *     and a customer-visible `Gift With Purchase` property, which is what
 *     carries the messaging through to checkout, the thank-you page and the
 *     order confirmation email.
 *   - `window.kpGwp.qualifyingSubtotal(cart)` and the `gwp:updated` event are
 *     the integration points for a cart progress bar.
 */
(function () {
  'use strict';

  if (window.__kpGwpLoaded) return;
  window.__kpGwpLoaded = true;

  const configEl = document.getElementById('gwp-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    console.error('[gwp] failed to parse config', err);
    return;
  }
  if (!config || !config.enabled || !config.variantId) return;

  const GIFT_VARIANT_ID = String(config.variantId);
  const GIFT_PRODUCT_ID = config.productId ? String(config.productId) : '';
  const THRESHOLD_CENTS = Number(config.threshold) || 0;
  const STARTS_AT = Number(config.startsAt) || 0;
  const ENDS_AT = Number(config.endsAt) || 0;
  const FREE_LABEL = config.freeLabel || 'Free';
  const GIFT_LABEL = config.giftLabel || '';
  const PROPERTY_NAME = config.propertyName || 'Gift With Purchase';
  const AUTO_GWP_FLAG = '_kp_auto_gwp';
  const OWNER_HEADER = 'X-Kp-Gwp';
  const RECONCILE_DEBOUNCE_MS = 250;

  if (!THRESHOLD_CENTS) return;

  // Set when the gift variant turns out to be unpurchasable, so we stop
  // retrying /cart/add.js on every cart event for the rest of the pageview.
  let disabled = false;

  // Locale-prefixed root (e.g. /en-gb) so cart calls stay inside the market.
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function cartUrl(path) {
    return ROOT.replace(/\/$/, '') + path;
  }

  // ---------- Item identity ----------

  function isGiftItem(item) {
    if (!item) return false;

    // The ownership flag is authoritative: it marks lines this engine added,
    // which is what we are allowed to remove.
    const properties = item.properties || item.props || null;
    if (properties && properties[AUTO_GWP_FLAG]) return true;

    // Fall back to identity so a manually-added gift (a $0 variant is
    // orderable by direct URL) is still recognised and locked.
    const variantId = item.variant_id || item.variantId || item.id;
    if (String(variantId) === GIFT_VARIANT_ID) return true;

    const productId = item.product_id || item.productId;
    return !!GIFT_PRODUCT_ID && String(productId) === GIFT_PRODUCT_ID;
  }

  // The $1 protection SKU must never count toward a promo threshold — see the
  // contract documented in assets/shipping-protection.js.
  function isProtectionItem(item) {
    return !!(
      window.kpShippingProtection &&
      typeof window.kpShippingProtection.isProtectionItem === 'function' &&
      window.kpShippingProtection.isProtectionItem(item)
    );
  }

  function isQualifyingItem(item) {
    return !isGiftItem(item) && !isProtectionItem(item);
  }

  // ---------- Qualifying subtotal ----------
  //
  // `cart.total_price` is net of automatic (cart- and line-level) discounts.
  // Shopify allocates those discounts down to the lines, so subtracting the
  // excluded lines' `final_line_price` keeps the arithmetic consistent.

  function qualifyingSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    let total = Number(cart.total_price) || 0;

    cart.items.forEach(function (item) {
      if (isQualifyingItem(item)) return;
      total -= Number(item.final_line_price) || 0;
    });

    return total > 0 ? total : 0;
  }

  function qualifyingItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(isQualifyingItem);
  }

  function findGiftItem(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    for (let i = 0; i < cart.items.length; i++) {
      if (isGiftItem(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  function inWindow() {
    const now = Math.floor(Date.now() / 1000);
    if (STARTS_AT && now < STARTS_AT) return false;
    if (ENDS_AT && now > ENDS_AT) return false;
    return true;
  }

  // ---------- Public namespace ----------

  window.kpGwp = {
    config: config,
    isGiftItem: isGiftItem,
    qualifyingSubtotal: qualifyingSubtotal,
    threshold: THRESHOLD_CENTS,
    inWindow: inWindow,
    filterCartItems: function (items) {
      if (!Array.isArray(items)) return items;
      return items.filter(function (item) {
        return !isGiftItem(item);
      });
    },
  };

  // ---------- Cart fetch (deduped) ----------

  let inFlightCart = null;

  function fetchCart() {
    if (inFlightCart) return inFlightCart;

    inFlightCart = fetch(cartUrl('/cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart fetch failed: ' + res.status);
        return res.json();
      })
      .finally(function () {
        // Release on the next tick so a burst of callers shares one response
        // but a subsequent reconcile still reads fresh state.
        setTimeout(function () {
          inFlightCart = null;
        }, 0);
      });

    return inFlightCart;
  }

  // ---------- Mutations ----------

  function addGift() {
    const properties = {};
    properties[AUTO_GWP_FLAG] = 'true';
    if (GIFT_LABEL) properties[PROPERTY_NAME] = GIFT_LABEL;

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    headers[OWNER_HEADER] = '1';

    return fetch(cartUrl('/cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers,
      body: JSON.stringify({
        items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: properties }],
      }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('gift add failed: ' + res.status);
        return true;
      })
      .catch(function (err) {
        // A sold-out or market-unavailable variant would otherwise retry on
        // every cart event; disable for this pageview instead.
        console.error('[gwp] could not add gift, disabling for this page', err);
        disabled = true;
        return false;
      });
  }

  function changeGift(item, quantity) {
    if (!item || !item.key) return Promise.resolve(false);

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    headers[OWNER_HEADER] = '1';

    return fetch(cartUrl('/cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers,
      body: JSON.stringify({ id: item.key, quantity: quantity }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('gift change failed: ' + res.status);
        return true;
      })
      .catch(function (err) {
        console.error('[gwp] gift change failed', err);
        return false;
      });
  }

  function notifyCartUpdated(cart) {
    document.dispatchEvent(
      new CustomEvent('gwp:updated', {
        detail: {
          cart: cart,
          qualifying: qualifyingSubtotal(cart),
          threshold: THRESHOLD_CENTS,
          unlocked: !!findGiftItem(cart),
        },
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart: cart, source: 'gwp' },
      })
    );

    if (typeof window.upcartRefreshCart === 'function') {
      window.upcartRefreshCart();
    }
  }

  // ---------- Reconcile (debounced + serialized) ----------
  //
  // Policy:
  //   - outside the campaign window: remove the gift if present, never add
  //   - no qualifying items at all: remove the gift
  //   - qualifying subtotal >= threshold: ensure exactly 1 gift
  //   - qualifying subtotal <  threshold: remove the gift
  //
  // The `reconciling` flag serializes concurrent calls; the debounce coalesces
  // bursts (UpCart's CartUpdated plus our own fetch hook both fire on one
  // customer action).

  let reconciling = false;
  let pendingReconcile = null;
  let pendingResolvers = [];

  function reconcileCart() {
    if (disabled) return Promise.resolve(false);

    if (pendingReconcile) {
      return new Promise(function (resolve) {
        pendingResolvers.push(resolve);
      });
    }

    return new Promise(function (resolve) {
      pendingResolvers.push(resolve);
      pendingReconcile = setTimeout(function () {
        pendingReconcile = null;
        const resolvers = pendingResolvers;
        pendingResolvers = [];
        runReconcile().then(function (result) {
          resolvers.forEach(function (r) {
            r(result);
          });
        });
      }, RECONCILE_DEBOUNCE_MS);
    });
  }

  function runReconcile() {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;

    return fetchCart()
      .then(function (cart) {
        const gift = findGiftItem(cart);

        if (!inWindow()) {
          if (gift) return changeGift(gift, 0);
          return false;
        }

        if (qualifyingItems(cart).length === 0) {
          if (gift) return changeGift(gift, 0);
          return false;
        }

        if (qualifyingSubtotal(cart) >= THRESHOLD_CENTS) {
          if (!gift) return addGift();
          if (gift.quantity > 1) return changeGift(gift, 1);
          return false;
        }

        if (gift) return changeGift(gift, 0);
        return false;
      })
      .then(function (changed) {
        if (!changed) {
          // No mutation, but the DOM may still be stale (first load with the
          // gift already in the cart, or an UpCart re-render).
          decorateAll();
          return false;
        }
        return fetchCart().then(function (cart) {
          decorateAll();
          notifyCartUpdated(cart);
          return true;
        });
      })
      .catch(function (err) {
        console.error('[gwp] reconcile failed', err);
        return false;
      })
      .finally(function () {
        reconciling = false;
      });
  }

  // ---------- DOM ----------

  // Matches a zero amount in the formats our currencies produce: $0.00,
  // 0,00 €, £0.00. The decimal part is required on purpose — a bare "0" would
  // otherwise catch stray quantity nodes and rewrite them to "FREE".
  const ZERO_MONEY = /^\s*[^\d]*0[.,]0{1,2}\s*[^\d]*\s*$/;

  function freeMarkup() {
    const span = document.createElement('span');
    span.className = 'kp-gwp__free';
    span.setAttribute('data-gwp-free', '');
    span.textContent = FREE_LABEL;
    return span;
  }

  // Replace a zero-price node with the FREE label. Returns true if a price
  // node was found, so the caller knows whether to fall back to a badge.
  function replaceZeroPrice(scope) {
    let replaced = false;

    const candidates = scope.querySelectorAll('span, div, p, s, del, strong, em, bdi');
    for (let i = 0; i < candidates.length; i++) {
      const el = candidates[i];
      if (el.querySelector('*')) continue; // leaf nodes only
      if (el.hasAttribute('data-gwp-free')) {
        replaced = true;
        continue;
      }
      if (!ZERO_MONEY.test(el.textContent || '')) continue;

      el.textContent = FREE_LABEL;
      el.classList.add('kp-gwp__free');
      el.setAttribute('data-gwp-free', '');
      replaced = true;
    }

    return replaced;
  }

  // UpCart renders the drawer; we don't own its markup and its class names are
  // not part of a documented contract, so target it structurally: the line is
  // found by the variant id in its product link (the approach already proven
  // in shipping-protection.js), and the controls by role rather than class.
  function decorateUpcart(root) {
    root.querySelectorAll('.upcart-public-cart-item').forEach(function (el) {
      const link = el.querySelector('a[href*="?variant="]') || el.querySelector('a[href]');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      const variantId = (href.split('?variant=')[1] || '').split('&')[0];
      if (!variantId || String(variantId) !== GIFT_VARIANT_ID) return;

      el.setAttribute('data-gwp-item', '');

      el.querySelectorAll(
        'input[type="number"], select, [class*="quantity" i], [class*="stepper" i], [class*="remove" i], [aria-label*="remove" i], [aria-label*="quantity" i]'
      ).forEach(function (control) {
        control.setAttribute('data-gwp-hide', '');
      });

      // UpCart does not honour Shopify's leading-underscore convention for line
      // item properties — it prints every one of them, so `_kp_auto_gwp` would
      // show up as a visible row. The theme's only existing defence is
      // `hideInstantRefProperty()` in assets/global.js, which matches
      // `_instant-ref` and nothing else. Hide ours the same way we hide the
      // controls, so the CSS in snippets/gwp.liquid covers it.
      el.querySelectorAll('.upcart-item-property').forEach(function (prop) {
        if ((prop.textContent || '').indexOf(AUTO_GWP_FLAG) !== -1) {
          prop.setAttribute('data-gwp-hide', '');
        }
      });

      if (!replaceZeroPrice(el) && !el.querySelector('[data-gwp-free]')) {
        const badge = document.createElement('span');
        badge.className = 'kp-gwp__badge';
        badge.setAttribute('data-gwp-free', '');
        badge.textContent = FREE_LABEL;

        const anchor = el.querySelector('a');
        if (anchor && anchor.parentNode) {
          anchor.insertAdjacentElement('afterend', badge);
        } else {
          el.appendChild(badge);
        }
      }
    });
  }

  // The native drawer and cart page are server-rendered by
  // snippets/cart-item.liquid, so they already carry `data-gwp-item` and the
  // FREE label. This only covers lines injected after a client-side re-render.
  function decorateNative(root) {
    root.querySelectorAll('.cart-item[data-id]').forEach(function (el) {
      if (String(el.getAttribute('data-id')) !== GIFT_VARIANT_ID) return;

      el.setAttribute('data-gwp-item', '');
      if (!replaceZeroPrice(el) && !el.querySelector('[data-gwp-free]')) {
        const content = el.querySelector('.cart-item__content') || el;
        content.appendChild(freeMarkup());
      }
    });
  }

  function decorateAll(root) {
    root = root || document;
    try {
      decorateUpcart(root);
      decorateNative(root);
    } catch (err) {
      console.error('[gwp] decorate failed', err);
    }
  }

  // ---------- Observer ----------
  //
  // UpCart rebuilds its DOM wholesale on every cart update and drops our
  // attributes with it, so re-decorate on mutation rather than once at boot.

  function setupObserver() {
    if (typeof MutationObserver !== 'function') return;

    let queued = false;
    const observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        decorateAll();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ---------- UpCart events ----------

  function subscribeToUpcart() {
    if (typeof window.upcartSubscribeAddedToCart === 'function') {
      window.upcartSubscribeAddedToCart(function (payload) {
        const item = payload && payload.item;
        if (item && isGiftItem(item)) return;
        reconcileCart();
      });
    }

    if (typeof window.upcartSubscribeCartUpdated === 'function') {
      window.upcartSubscribeCartUpdated(function () {
        decorateAll();
        reconcileCart();
      });
    }

    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      window.upcartSubscribeCartLoaded(function () {
        decorateAll();
        reconcileCart();
      });
    }

    if (typeof window.upcartSubscribeItemRemoved === 'function') {
      window.upcartSubscribeItemRemoved(function () {
        reconcileCart();
      });
    }
  }

  // ---------- Native cart events ----------

  function setupNativeListeners() {
    document.addEventListener('cart:updated', function (event) {
      // Ignore our own dispatch to avoid a feedback loop.
      if (event && event.detail && event.detail.source === 'gwp') return;
      reconcileCart();
    });
  }

  // ---------- Fetch interceptor ----------
  //
  // Third-party cart mutations (UpCart, Recharge, app blocks) must trigger a
  // reconcile. Our own calls are tagged with a header so we don't bounce off
  // ourselves.

  function installFetchHook() {
    if (typeof window.fetch !== 'function') return;
    if (window.__kpGwpFetchHook) return;
    window.__kpGwpFetchHook = true;

    const origFetch = window.fetch.bind(window);

    window.fetch = function (resource, init) {
      let url = '';
      if (typeof resource === 'string') {
        url = resource;
      } else if (resource && resource.url) {
        url = resource.url;
      }

      const isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
      let isOurOwnCall = false;

      if (init) {
        if (typeof init.body === 'string' && init.body.indexOf(AUTO_GWP_FLAG) !== -1) {
          isOurOwnCall = true;
        }
        if (init.headers) {
          const headerVal =
            typeof init.headers.get === 'function'
              ? init.headers.get(OWNER_HEADER)
              : init.headers[OWNER_HEADER];
          if (headerVal) isOurOwnCall = true;
        }
      }

      const promise = origFetch(resource, init);

      if (isCartMutation && !isOurOwnCall) {
        promise.then(
          function (res) {
            if (res && res.ok) reconcileCart();
          },
          function () {}
        );
      }

      return promise;
    };
  }

  // ---------- Boot ----------

  function init() {
    if (window.location.pathname.indexOf('/checkout') === 0) return;

    decorateAll();
    setupObserver();
    subscribeToUpcart();
    setupNativeListeners();
    installFetchHook();
    reconcileCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
